import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import "./ExitPopup.css";
import popupGlow from "../assets/assets/images/popup-glow.svg";
import viriqoHiresLogo from "../../interviewAI/assets/newlogo/VIRIQO_HIRES_Logo.png";
import resourceLibrary from "../assets/ExitPopup/ai-hiring-resource-library.svg";
import actionableGuides from "../assets/assets/icons/popup/actionable-guides.svg";
import templatesChecklists from "../assets/assets/icons/popup/templates-checklists.svg";
import latestTrends from "../assets/assets/icons/popup/latest-trends.svg";
import realUseCases from "../assets/assets/icons/popup/real-use-cases.svg";
import testimonialPhoto from "../assets/assets/images/testimonial-priya-sharma.png";
import { completeJourney, createTrackingContext, submitExitPopup } from "../services/webTracking";

const ExitPopup = ({ blocked = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const shown = useRef(false);
  const blockedRef = useRef(blocked);
  const dialogRef = useRef(null);
  const dismiss = () => setOpen(false);
  blockedRef.current = blocked;

  useEffect(() => {
    let timer;
    const attempt = () => {
      if (shown.current) return;
      const anotherDialog = [...document.querySelectorAll('[aria-modal="true"]')].some((node) => {
        const style = window.getComputedStyle(node);
        return style.display !== "none" && style.visibility !== "hidden" && node.getClientRects().length > 0;
      });
      if (blockedRef.current || anotherDialog || document.hidden || document.body.style.overflow === "hidden") {
        timer = window.setTimeout(attempt, 1000);
        return;
      }
      shown.current = true;
      setOpen(true);
    };
    timer = window.setTimeout(attempt, 30000);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (blocked) setOpen(false);
  }, [blocked]);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const previousRootOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    const dialog = dialogRef.current;
    // Hide background content from keyboard and screen reader navigation.
    const background = [...document.body.children].filter(node => !node.contains(dialog) && node.tagName !== "SCRIPT");
    const previousInert = background.map(node => node.inert);
    background.forEach(node => { node.inert = true; });
    dialog.focus({ preventScroll: true });
    const onKeyDown = event => {
      if (event.key === "Escape") { event.preventDefault(); dismiss(); }
      if (event.key !== "Tab") return;
      const focusable = [...dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), [tabindex="0"]')].filter(node => node.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog)) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      background.forEach((node, i) => { node.inert = previousInert[i]; });
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousRootOverflow;
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const names = [form.elements.first_name, form.elements.last_name];
    names.forEach((field) => field.setCustomValidity(field.value.trim() ? "" : "Please enter your name."));
    if (!form.reportValidity()) return;

    setSubmitting(true);
    setStatus("");
    try {
      await submitExitPopup({
        first_name: form.elements.first_name.value.trim(),
        last_name: form.elements.last_name.value.trim(),
        job_title: form.elements.job_title.value || null,
        company: form.elements.company.value.trim() || null,
        consent: form.elements.consent.checked,
        tracking: createTrackingContext({
          cta: "Yes, Keep Me Updated",
          organization: form.elements.company.value,
        }),
      });
      completeJourney();
      form.reset();
      setOpen(false);
      navigate("/demo-thankyou");
    } catch (error) {
      setStatus(error.message || "We couldn’t submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return createPortal(
    <div className="tt-exit-popup is-open">
      <div className="tt-exit-popup-backdrop" onClick={dismiss} aria-hidden="true" />
      <div className="tt-exit-popup-scroller">
        <div className="tt-exit-popup-dialog" ref={dialogRef} role="dialog" tabIndex={-1} aria-modal="true" aria-labelledby="exit-popup-title" aria-describedby="exit-popup-desc">
        <button type="button" className="tt-exit-close" aria-label="Close popup" onClick={dismiss}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <img className="tt-exit-popup-glow" src={popupGlow} alt="" aria-hidden="true" />

        
        <div className="tt-exit-popup-main">
          <img className="tt-exit-popup-logo" src={viriqoHiresLogo} alt="Viriqo Hires" width="214" height="30" />
          <span className="tt-exit-popup-rule" aria-hidden="true"></span>

          <p className="tt-exit-popup-eyebrow">Wait! Before you go...</p>
          <h2 className="tt-exit-popup-title" id="exit-popup-title">Get Expert Insights on <span className="accent">AI in Technical Hiring</span></h2>
          <p className="tt-exit-popup-sub" id="exit-popup-desc">Sign in to continue building high-performing teams with AI.</p>

          <form className="tt-exit-form" onSubmit={handleSubmit}>
            <div className="tt-exit-form-row">
              <div className="tt-exit-field">
                <label htmlFor="ep-first">First Name <span className="req" aria-hidden="true">*</span></label>
                <input type="text" id="ep-first" name="first_name" onInput={event => event.currentTarget.setCustomValidity("")} placeholder="John" autoComplete="given-name" required />
              </div>
              <div className="tt-exit-field">
                <label htmlFor="ep-last">Last Name <span className="req" aria-hidden="true">*</span></label>
                <input type="text" id="ep-last" name="last_name" onInput={event => event.currentTarget.setCustomValidity("")} placeholder="Doe" autoComplete="family-name" required />
              </div>
            </div>
            <div className="tt-exit-field">
              <label htmlFor="ep-job">Job Title</label>
              
              <select id="ep-job" name="job_title" defaultValue="" className="is-placeholder" onChange={event => event.currentTarget.classList.toggle("is-placeholder", !event.currentTarget.value)}>
                <option value="">Select your job title</option>
                <option>Recruiter</option>
                <option>Talent Acquisition Manager</option>
                <option>HR Manager</option>
                <option>Hiring Manager</option>
                <option>Engineering Leader</option>
                <option>Other</option>
              </select>
            </div>
            <div className="tt-exit-field">
              <label htmlFor="ep-company">Company Name</label>
              <input type="text" id="ep-company" name="company" placeholder="Acme Inc." autoComplete="organization" />
            </div>

            <label className="tt-exit-consent">
              <input type="checkbox" name="consent" />
              <span>I agree to receive periodic communications from Viriqo Hires.<br /> I can read the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</span>
            </label>

            <button type="submit" className="tt-exit-submit" disabled={submitting}>
              <span>{submitting ? "Submitting…" : "Yes, Keep Me Updated"}</span>
              <svg width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true"><path d="M1 6h11.5M7.5 1l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <p className="tt-exit-form-status" role="status">{status}</p>
          </form>

          <button type="button" className="tt-exit-dismiss" onClick={dismiss}>
            <span>No thanks, I&rsquo;ll continue to leave</span>
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true"><path d="M1 6h13.5M9.5 1l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>

        
        <div className="tt-exit-popup-aside">
          <div className="tt-exit-popup-visual">
            <img src={resourceLibrary} alt="Viriqo Hires AI Hiring Resource Library with a Technical Hiring Checklist and Industry Insights" width="566" height="420" />
          </div>

          <ul className="tt-exit-benefits">
            <li><img src={actionableGuides} alt="" width="45" height="45" />Actionable guides and frameworks</li>
            <li><img src={templatesChecklists} alt="" width="45" height="45" />Ready-to-use templates and checklists</li>
            <li><img src={latestTrends} alt="" width="45" height="45" />Latest trends in AI hiring and technical talent</li>
            <li><img src={realUseCases} alt="" width="45" height="45" />Real use cases from industry leaders</li>
          </ul>

          <figure className="tt-exit-testimonial">
            <img className="tt-exit-testimonial-photo" src={testimonialPhoto} alt="Priya Sharma" width="92" height="92" />
            <svg className="tt-exit-testimonial-mark" width="40" height="35" viewBox="0 0 40 35" aria-hidden="true"><path fill="currentColor" d="M0 35V21.5C0 11.2 3.9 4 13.2 0l3.1 5.2C11.6 7.6 9.3 11 8.9 15.6h8.3V35H0zm22.8 0V21.5C22.8 11.2 26.7 4 36 0l3.1 5.2c-4.7 2.4-7 5.8-7.4 10.4H40V35H22.8z"/></svg>
            <div className="tt-exit-testimonial-body">
              <blockquote>"The insights from Viriqo Hires help us stay ahead in technical hiring. Practical, relevant and easy to implement."</blockquote>
              <figcaption><strong>Priya Sharma</strong><span>Director, Talent Acquisition</span></figcaption>
            </div>
          </figure>
        </div>
        </div>
      </div>
    </div>, document.body
  );
};

export default ExitPopup;
